<template>
    <div class="grid-content" v-if="isReady">
        <div class="grid-item no-flex">
            <v-popper2 position="bottomStart" :close-function="updateFilterOnClickOutside" :state="showFilterPopper" :offset="0" ref="filterPopper">
                <template #trigger="{popper}">
                    <v-icon-button @click="openFilterList(popper)" class="stack-xs">
                        Add Filter
                        <v-icon icon="caret-down" slot="right-icon"/>
                    </v-icon-button>
                </template>
                <template #content="{popper}">
                    <div class="dropdown z-top">
                        <div class="dropdown-content dropdown-content-padded">
                            <v-select ref="filterList"
                                      placeholder="Search"
                                      :options="filterOptions"
                                      :preserve-search="false"
                                      :value="localSelection"
                                      @input="selectFilter"/>
                        </div>
                        <div v-if="selectedFilter">
                            <component :is="getComponentType(selectedFilter.filter)"
                                       :key="selectedFilter.filter.label"
                                       ref="filterComponent"
                                       :filter="selectedFilter"
                                       :event-bus="eventBus"
                                       :adapter="adapter"
                                       :inline="false"
                                       @update="updateActiveFilter">
                            </component>
                        </div>
                    </div>
                </template>
            </v-popper2>
        </div>
        <div class="grid-item grid-item-compact">
            <v-popper2
                v-for="(activeFilter, activeFilterIndex) in compiledFilters"
                :close-function="updateActiveFilterOnClickOutside"
                :key="activeFilter.uid"
                ref="activeFilterPopper"
            >
                <template #trigger="{popper}">
                    <div class="chip chip-hoverable has-delete" @click="() => {activeFilterIndexCache = activeFilterIndex; popper.open()}">
                        <filter-summary :condition="activeFilter.condition" :resource-field="activeFilter.filter"/>
                        <button class="btn btn-link" @click="removeActiveFilter($event, activeFilterIndex)">
                            <v-icon icon="close-s"/>
                        </button>
                    </div>
                </template>
                <template #content="{popper}">
                    <div class="dropdown z-top">
                        <component :is="getComponentType(activeFilter.filter)"
                            :key="activeFilter.filter.label"
                            :filter="activeFilter"
                            :event-bus="eventBus"
                            :adapter="adapter"
                            :inline="false"
                            ref="activeFilterComponent"
                            @update="updateActiveFilter($event, activeFilterIndex)">
                        </component>
                    </div>
                </template>
            </v-popper2>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import vIcon from '@/components/form/v-icon';
    import vIconButton from '@/components/form/v-icon-button';
    import vSelect from '@/components/form/v-select';
    import vPopper2 from '@/components/popper/v-popper2';
    import FilterSummary from '@/components/query/filter-summary';
    import FILTER_TYPES from './filter-types';
    import TextFilter from './components/filters/text-filter';
    import BooleanFilter from './components/filters/boolean-filter';
    import EnumFilter from './components/filters/enum-filter';
    import NumericFilter from './components/filters/numeric-filter';
    import SelectFilter from './components/filters/select-filter';
    import DatetimeFilter from './components/filters/datetime-filter';
    import labelFromJsonPointer from './helpers/label-from-json-pointer';
    import CriteriaAdapter from './adapters/criteria/criteria-adapter';
    import FilterAdapter from './adapters/filter/filter-adapter';

    const QueryTypes = {
        filter: 'filter',
        criteria: 'criteria',
    };

    /**
     * This component controls and renders filter selection and editing for the segments.vue component.
     */
    export default {
        components: {
            vIcon,
            vSelect,
            vPopper2,
            vIconButton,
            TextFilter,
            BooleanFilter,
            EnumFilter,
            NumericFilter,
            SelectFilter,
            DatetimeFilter,
            FilterSummary,
        },
        props: {
            options: {
                type: Object,
                default: () => ({}),
            },
            /**
             * An array of filters, usually contains one filter represented as an array.
             */
            query: {
                type: [Object, Array],
                default: () => ({}),
            },
            /**
             * Determine whether we are working in the context of (deprecated) Criteria or Filters.
             */
            type: {
                type: String,
                default: QueryTypes.filter,
                validator: type => [QueryTypes.filter, QueryTypes.criteria]
                    .includes(type),
            },
        },
        computed: {
            /**
             * Retrieve information about each filter as defined in a filter set.
             */
            filterOptions() {
                if (this.filterSet) {
                    return Object
                        .keys(this.filterSet)
                        .sort()
                        .map((jsonPointer) => {
                            const filter = this.filterSet[jsonPointer];
                            return {
                                label: filter.label || labelFromJsonPointer(jsonPointer),
                                value: jsonPointer,
                            };
                        });
                }
                return [];
            },
            hasQuery() {
                if (this.query) {
                    return Object.keys(this.query).length;
                }
                return false;
            },
            /**
             * Get an adapter for the current component instance based on its `type`.
             * @returns {FilterAdapter|CriteriaAdapter}
             */
            adapter() {
                if (this.type === QueryTypes.filter) {
                    return new FilterAdapter();
                }
                return new CriteriaAdapter();
            },
            /**
             * Return a filter query.
             * @see query
             * @returns {Array}
             */
            compiledFilters() {
                if (this.hasQuery) {
                    const params = {query: this.query, filterSet: this.filterSet};
                    this.activeFilters = this.adapter.parseQuery(params);
                    return this.activeFilters;
                }
                // reset filters when the query is empty
                this.activeFilters = [];
                return [];
            },
            hasSelectedFilter() {
                return this.selectedFilter !== null;
            },
        },
        data() {
            return {
                isReady: false,
                filterSet: null,
                activeFilters: [],
                filterTypes: {...FILTER_TYPES},
                eventBus: new Vue(),
                selectedFilter: null,
                localSelection: null,
                showFilterPopper: null,
                showEditFilterPopper: null,
                activeFilterIndexCache: null,
            };
        },
        methods: {
            /**
             * Determine which component to mount, i.e. if a filter is a text value, return a
             * text-input based component. If the filter is true or false, return a boolean-
             * based component.
             * @param type
             * @returns {*}
             */
            getComponentType({type}) {
                if (type === FILTER_TYPES.text) {
                    return TextFilter;
                }
                if (type === FILTER_TYPES.boolean) {
                    return BooleanFilter;
                }
                if (type === FILTER_TYPES.enum) {
                    return EnumFilter;
                }
                if (type === FILTER_TYPES.numeric) {
                    return NumericFilter;
                }
                if (type === FILTER_TYPES.select) {
                    return SelectFilter;
                }
                if (type === FILTER_TYPES.datetime) {
                    return DatetimeFilter;
                }
                // fallback
                return TextFilter;
            },
            openFilterList(popper) {
                this.resetSelectedFilter();
                popper.toggle();
                if (!popper.isVisible && !this.hasSelectedFilter) {
                    this.$nextTick(() => {
                        this.activateFilterList();
                    });
                }
            },
            activateFilterList() {
                this.$refs.filterList.activate();
                this.$refs.filterList.focus();
            },
            resetSelectedFilter() {
                this.selectedFilter = null;
                this.localSelection = null;
            },
            /**
             * Return the filter set after all potential values or other discoveries are required
             * depending on the filter type.
             */
            prepareFilters() {
                (async () => {
                    this.filterSet = await this.options.filterSet.getFilters();
                    this.isReady = true;
                    this.$emit('ready');
                })();
            },
            /**
             * Set the selectedFilter encapsulated in the ActiveFilter class from the user-selected
             * filter.
             * @see FilterAdapter#createActiveFilter
             * @see ActiveFilter
             * @param data
             */
            selectFilter(data = null) {
                if (data && this.filterSet[data]) {
                    this.localSelection = data;
                    this.selectedFilter = this.adapter.createActiveFilter(this.filterSet[data]);
                } else if (data === null) {
                    this.resetSelectedFilter();
                }
            },
            /**
             * Remove filter from the list of active filters (i.e. no longer include this filter in
             * the Segment.)
             * @param event
             * @param activeFilterIndex int representing the index in activeFilters array
             */
            removeActiveFilter(event, activeFilterIndex) {
                this.activeFilters.splice(activeFilterIndex, 1);
                this.showFilterPopper = false;
                this.$nextTick(() => { this.showFilterPopper = null; });
                this.updateQuery();
                event.preventDefault();
            },
            /**
             * Update the currently selectedFilter's conditions with the values input by user.
             * @param data
             * @param index
             */
            updateActiveFilter({data}, index = null) {
                const filter = index !== null ? this.activeFilters[index] : this.selectedFilter;
                filter.condition = data;
                if (index === null) {
                    this.activeFilters.push(filter);
                    this.resetSelectedFilter();
                    this.showFilterPopper = false;
                    this.$nextTick(() => {
                        this.showFilterPopper = null;
                    });
                } else {
                    this.showEditFilterPopper = false;
                    this.$nextTick(() => {
                        this.showEditFilterPopper = null;
                    });
                }
                this.updateQuery();
            },
            /**
             * Push the array of activeFilters up the DOM stack which will eventually be saved in
             * user preferences (done in segments.vue)
             */
            updateQuery() {
                const criteria = this.adapter.getQueryFromActiveFilters(this.activeFilters);
                // the side effect of this update is the re-computation of `compiledFilters`
                // which is used for rendering filters in the component
                this.$emit('update', criteria);
            },
            updateFilterOnClickOutside() {
                return new Promise((resolve) => {
                    if (this.hasSelectedFilter) {
                        this.$refs.filterComponent.updateFilter();
                    }
                    resolve();
                });
            },
            updateActiveFilterOnClickOutside() {
                return new Promise((resolve) => {
                    const activeFilterPresent = (this.$refs.activeFilterComponent !== undefined
                        && this.$refs.activeFilterComponent.length !== 0);
                    if (activeFilterPresent
                        && this.$refs.activeFilterPopper[this.activeFilterIndexCache].popper.isVisible) {
                        this.$refs.activeFilterComponent[0].updateFilter();
                    }
                    resolve();
                });
            },
        },
        created() {
            this.prepareFilters();
        },
        beforeDestroy() {
            this.eventBus = null;
        },
    };
</script>
